/**
 * Octree progressive subdivision in JavaScript.
 *
 * Port of `OctreeProbabilityFixedlenDecoder.sample` in model.py. The
 * transformer step (predicting 8-way child-cell logits) lives in ONNX
 * (octree_step.onnx); the loop, sampling, and bookkeeping live here.
 *
 * For each level 1..MAX_LEVEL we:
 *   - feed current parent cells + remaining counts to the octree net
 *   - softmax + sample with systematic sampling
 *   - keep cells that received >0 samples
 *   - subdivide their coordinates into the next level
 */
import * as ort from "onnxruntime-web";
import type { InferenceSession, Tensor } from "onnxruntime-web";
import { safeRun, tensorFloat32 } from "./models";

export const MAX_LEVEL = 8;

const CHILD_OFFSETS_FLAT = new Int32Array([
  0, 0, 0,  1, 0, 0,  0, 1, 0,  1, 1, 0,
  0, 0, 1,  1, 0, 1,  0, 1, 1,  1, 1, 1,
]);

/**
 * Returns final-level point cloud (B, N, 3) in [0, 1] alongside their counts.
 * For our pipeline B = 1 always.
 */
export async function sampleOctree(
  octree: InferenceSession,
  cond: Tensor,
  numPoints: number,
  temperature = 1.0,
): Promise<Float32Array> {
  let prevCoords = new Int32Array([0, 0, 0]); // (L, 3) flat
  let prevCounts = new Int32Array([numPoints]);
  let L = 1;

  const numTensor = new ort.Tensor("int64", BigInt64Array.from([BigInt(numPoints)]), [1]);

  for (let lv = 1; lv <= MAX_LEVEL; lv++) {
    const resP = 1 << (lv - 1);
    const res  = 1 << lv;

    // safeRun will auto-cast points/cond to whatever dtype the octree
    // variant in use wants (fp16 vs fp32).
    const pnorm = new Float32Array(L * 3);
    for (let i = 0; i < L * 3; i++) pnorm[i] = (prevCoords[i] + 0.5) / resP;
    const pTensor = new ort.Tensor("float32", pnorm, [1, L, 3]);
    const lTensor = new ort.Tensor("int64", BigInt64Array.from([BigInt(res)]), [1]);

    // Some export variants drop the optional `l2` input (constant-folded away
    // because it's traced with a constant tensor). Only pass it if the model
    // declares it as an input.
    const feeds: Record<string, ort.Tensor> = { points: pTensor, l: lTensor, cond };
    if ((octree.inputNames as readonly string[]).includes("l2")) {
      feeds.l2 = numTensor;
    }
    const out = await safeRun(octree, feeds);
    const logits = out.logits as Tensor;          // (1, L, 8), dtype follows model
    const logitsF32 = tensorFloat32(logits);      // decode fp16 if needed
    const probs  = softmaxLastAxis(logitsF32, L, 8);
    const sampled = systematicSample(probs, prevCounts, 8, temperature);

    // expand parent cells into children and keep occupied ones
    const next = expand(prevCoords, prevCounts, sampled, L);
    prevCoords = next.coords;
    prevCounts = next.counts;
    L = next.L;
  }

  // expand each level-MAX cell into N copies of itself with sub-voxel jitter
  const final = new Float32Array(numPoints * 3);
  const res = 1 << MAX_LEVEL;
  let writeIdx = 0;
  for (let i = 0; i < L; i++) {
    const cx = prevCoords[i * 3 + 0], cy = prevCoords[i * 3 + 1], cz = prevCoords[i * 3 + 2];
    const n = prevCounts[i];
    for (let k = 0; k < n; k++) {
      final[writeIdx++] = (cx + Math.random()) / res;
      final[writeIdx++] = (cy + Math.random()) / res;
      final[writeIdx++] = (cz + Math.random()) / res;
    }
  }
  return final;
}

// ----- internals ------------------------------------------------------------

function softmaxLastAxis(x: Float32Array, L: number, C: number): Float32Array {
  const out = new Float32Array(L * C);
  for (let i = 0; i < L; i++) {
    let max = -Infinity;
    for (let c = 0; c < C; c++) {
      const v = x[i * C + c]; if (v > max) max = v;
    }
    let sum = 0;
    for (let c = 0; c < C; c++) {
      const e = Math.exp(x[i * C + c] - max);
      out[i * C + c] = e; sum += e;
    }
    const inv = 1 / sum;
    for (let c = 0; c < C; c++) out[i * C + c] *= inv;
  }
  return out;
}

/** Systematic sampling: draw `count[i]` discrete samples from a row of probs. */
function systematicSample(probs: Float32Array, counts: Int32Array,
                          C: number, T = 1.0): Int32Array {
  const L = counts.length;
  const out = new Int32Array(L * C);
  for (let i = 0; i < L; i++) {
    const n = counts[i];
    if (n === 0) continue;
    // build CDF for row i (apply temperature: probs^(1/T) / sum)
    let cdf = new Float32Array(C);
    let sum = 0;
    for (let c = 0; c < C; c++) {
      const v = Math.pow(Math.max(probs[i * C + c], 1e-20), 1 / T);
      sum += v; cdf[c] = sum;
    }
    if (sum === 0) {
      // uniform fallback
      const each = Math.floor(n / C), rem = n - each * C;
      for (let c = 0; c < C; c++) out[i * C + c] = each + (c < rem ? 1 : 0);
      continue;
    }
    for (let c = 0; c < C; c++) cdf[c] = Math.min(cdf[c] / sum, 1 - 1e-12);
    // n stratified samples
    const u0 = Math.random() / n;
    let cur = 0;
    for (let k = 0; k < n; k++) {
      const u = u0 + k / n;
      while (cur < C - 1 && u > cdf[cur]) cur++;
      out[i * C + cur]++;
    }
  }
  return out;
}

function expand(prevCoords: Int32Array, prevCounts: Int32Array,
                sampled: Int32Array, L: number) {
  // count occupied children
  let kept = 0;
  for (let i = 0; i < L * 8; i++) if (sampled[i] > 0) kept++;
  const coords = new Int32Array(kept * 3);
  const counts = new Int32Array(kept);
  let w = 0;
  for (let i = 0; i < L; i++) {
    const px = prevCoords[i * 3 + 0], py = prevCoords[i * 3 + 1], pz = prevCoords[i * 3 + 2];
    for (let c = 0; c < 8; c++) {
      const n = sampled[i * 8 + c];
      if (n === 0) continue;
      coords[w * 3 + 0] = px * 2 + CHILD_OFFSETS_FLAT[c * 3 + 0];
      coords[w * 3 + 1] = py * 2 + CHILD_OFFSETS_FLAT[c * 3 + 1];
      coords[w * 3 + 2] = pz * 2 + CHILD_OFFSETS_FLAT[c * 3 + 2];
      counts[w] = n;
      w++;
    }
  }
  return { coords, counts, L: kept };
}
